import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarCheck,
  DollarSign,
  Gauge,
  MapPin,
  Plus,
  Package,
  FileBarChart,
  Bell,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { CompleteProfileDialog } from "@/components/merchant/complete-profile-dialog";
import { UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/")({
  head: () => ({
    meta: [
      { title: "Merchant Dashboard — Spayzo" },
      { name: "description", content: "Manage bookings, spaces, and revenue performance." },
    ],
  }),
  component: MerchantDashboard,
});

const revenueByRange: Record<"7D" | "30D" | "90D", { d: string; v: number }[]> = {
  "7D": [
    { d: "Mon", v: 1200 },
    { d: "Tue", v: 1850 },
    { d: "Wed", v: 1640 },
    { d: "Thu", v: 2280 },
    { d: "Fri", v: 2940 },
    { d: "Sat", v: 3320 },
    { d: "Sun", v: 2780 },
  ],
  "30D": [
    { d: "W1", v: 8200 },
    { d: "W2", v: 10450 },
    { d: "W3", v: 9680 },
    { d: "W4", v: 12840 },
    { d: "W5", v: 14120 },
  ],
  "90D": [
    { d: "Jan", v: 28400 },
    { d: "Feb", v: 32100 },
    { d: "Mar", v: 41250 },
  ],
};
const bookingsByRange: Record<"7D" | "30D" | "90D", { d: string; v: number }[]> = {
  "7D": [
    { d: "Mon", v: 14 },
    { d: "Tue", v: 22 },
    { d: "Wed", v: 18 },
    { d: "Thu", v: 27 },
    { d: "Fri", v: 34 },
    { d: "Sat", v: 41 },
    { d: "Sun", v: 30 },
  ],
  "30D": [
    { d: "W1", v: 96 },
    { d: "W2", v: 128 },
    { d: "W3", v: 114 },
    { d: "W4", v: 152 },
    { d: "W5", v: 168 },
  ],
  "90D": [
    { d: "Jan", v: 342 },
    { d: "Feb", v: 398 },
    { d: "Mar", v: 471 },
  ],
};

type Kpi = {
  labelKey: string;
  value: React.ReactNode;
  delta: number;
  icon: React.ComponentType<{ className?: string }>;
  hintKey: string;
};

const kpis: Kpi[] = [
  { labelKey: "merchantIndex.kpi.totalBookings", value: "186", delta: 12.4, icon: CalendarCheck, hintKey: "merchantIndex.kpi.vsLastWeek" },
  { labelKey: "merchantIndex.kpi.revenue", value: <Money value="14,820" />, delta: 8.1, icon: DollarSign, hintKey: "merchantIndex.kpi.vsLastWeek" },
  { labelKey: "merchantIndex.kpi.occupancyRate", value: "73%", delta: -2.3, icon: Gauge, hintKey: "merchantIndex.kpi.vsLastWeek" },
  { labelKey: "merchantIndex.kpi.activeSpaces", value: "12", delta: 2, icon: MapPin, hintKey: "merchantIndex.kpi.newThisMonth" },
];

function MerchantDashboard() {
  const { t } = useTranslation();
  const [range, setRange] = useState<"7D" | "30D" | "90D">("7D");
  const revenueData = revenueByRange[range];
  const bookingsData = bookingsByRange[range];
  const revenueTotal = useMemo(() => revenueData.reduce((s, r) => s + r.v, 0), [revenueData]);
  const bookingsTotal = useMemo(() => bookingsData.reduce((s, r) => s + r.v, 0), [bookingsData]);
  const rangeLabels: Record<"7D" | "30D" | "90D", string> = {
    "7D": t("merchantIndex.range.thisWeek"),
    "30D": t("merchantIndex.range.last30Days"),
    "90D": t("merchantIndex.range.last90Days"),
  };
  return (
    <MerchantShell
      title={t("merchantIndex.title")}
      subtitle={t("merchantIndex.subtitle")}
      actions={
        <CompleteProfileDialog
          trigger={
            <Button className="h-10 rounded-xl text-white shadow-soft hover:opacity-90" style={{ backgroundColor: "#5958F3" }}>
              <UserCheck className="h-4 w-4" /> {t("merchantIndex.completeProfile")}
            </Button>
          }
        />
      }
    >
      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => {
          const positive = k.delta >= 0;
          const Icon = k.icon;
          return (
            <Card key={k.labelKey} className="rounded-2xl border-border/60 p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
                    positive ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600",
                  )}
                >
                  {positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {Math.abs(k.delta)}%
                </span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">{t(k.labelKey)}</p>
              <p className="mt-1 font-display text-3xl font-bold tracking-tight">{k.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t(k.hintKey)}</p>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t("merchantIndex.revenueTrend")}</p>
              <p className="mt-1 font-display text-2xl font-bold"><Riyal /> {revenueTotal.toLocaleString()}</p>
            </div>
            <Tabs value={range} onValueChange={(v) => setRange(v as "7D" | "30D" | "90D")}>
              <TabsList className="rounded-xl">
                {(["7D", "30D", "90D"] as const).map((tt) => (
                  <TabsTrigger key={tt} value={tt} className="rounded-lg text-xs font-semibold">
                    {tt}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="mt-4 h-64 w-full">
            <ResponsiveContainer>
              <AreaChart data={revenueData} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.58 0.17 262)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="oklch(0.58 0.17 262)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="oklch(0.92 0.008 260)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="d" tickLine={false} axisLine={false} className="text-xs" />
                <YAxis tickLine={false} axisLine={false} className="text-xs" />
                <RTooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="v" stroke="oklch(0.58 0.17 262)" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t("merchantIndex.bookingActivity")}</p>
              <p className="mt-1 font-display text-2xl font-bold">{bookingsTotal}</p>
            </div>
            <Badge variant="secondary" className="rounded-full">
              {rangeLabels[range]}
            </Badge>
          </div>
          <div className="mt-4 h-64 w-full">
            <ResponsiveContainer>
              <BarChart data={bookingsData} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="oklch(0.92 0.008 260)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="d" tickLine={false} axisLine={false} className="text-xs" />
                <YAxis tickLine={false} axisLine={false} className="text-xs" />
                <RTooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="v" radius={[8, 8, 0, 0]} fill="oklch(0.58 0.17 262)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Widgets */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <RecentBookings />
        <UpcomingReservations />
        <AlertsPanel />
      </div>

      {/* Quick actions */}
      <Card className="mt-6 rounded-2xl border-border/60 p-5 shadow-soft">
        <p className="text-sm font-semibold">{t("merchantIndex.quickActions")}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t("merchantIndex.quickActionsSubtitle")}</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <QuickAction icon={MapPin} label={t("merchantIndex.addNewSpace")} hint={t("merchantIndex.addNewSpaceHint")} to="/merchant/spaces" />
          <QuickAction icon={Package} label={t("merchantIndex.addProduct")} hint={t("merchantIndex.addProductHint")} to="/merchant/products" />
          <QuickAction icon={FileBarChart} label={t("merchantIndex.viewReports")} hint={t("merchantIndex.viewReportsHint")} to="/merchant/analytics" />
        </div>
      </Card>
    </MerchantShell>
  );
}

function QuickAction({
  icon: Icon,
  label,
  hint,
  to,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-xl border border-border/60 bg-background p-4 transition hover:border-primary/40 hover:shadow-soft"
    >
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{label}</p>
        <p className="truncate text-xs text-muted-foreground">{hint}</p>
      </div>
      <Plus className="h-4 w-4 text-muted-foreground transition group-hover:text-primary" />
    </Link>
  );
}

const recent = [
  { name: "Sophie Tan", spaceKey: "studioA", timeKey: "hours2Ago", amount: <Money value="84" />, statusKey: "paid" },
  { name: "Marc Diallo", spaceKey: "meetingLoft", timeKey: "hours4Ago", amount: <Money value="152" />, statusKey: "paid" },
  { name: "Ines Rivas", spaceKey: "quietDesk3", timeKey: "yesterday", amount: <Money value="24" />, statusKey: "refunded" },
  { name: "Yuki Sato", spaceKey: "photoBay", timeKey: "yesterday", amount: <Money value="210" />, statusKey: "paid" },
];

function RecentBookings() {
  const { t } = useTranslation();
  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{t("merchantIndex.recentBookings")}</p>
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
          {t("merchantIndex.viewAll")}
        </Button>
      </div>
      <ul className="mt-3 divide-y divide-border/60">
        {recent.map((r) => (
          <li key={r.name} className="flex items-center gap-3 py-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {r.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{r.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {t(`merchantIndex.spaces.${r.spaceKey}`)} · {t(`merchantIndex.time.${r.timeKey}`)}
              </p>
            </div>
            <div className="text-end">
              <p className="text-sm font-semibold">{r.amount}</p>
              <p
                className={cn("text-[10px] font-semibold", r.statusKey === "paid" ? "text-emerald-600" : "text-rose-600")}
              >
                {t(`merchantIndex.status.${r.statusKey}`)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const upcoming = [
  { time: "10:00", spaceKey: "studioA", guest: "Sophie Tan", dur: "2h" },
  { time: "12:30", spaceKey: "photoBay", guest: "Yuki Sato", dur: "3h" },
  { time: "15:00", spaceKey: "meetingLoft", guest: "Marc Diallo", dur: "1h" },
  { time: "17:30", spaceKey: "quietDesk3", guest: "Ines Rivas", dur: "1h" },
];

function UpcomingReservations() {
  const { t } = useTranslation();
  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{t("merchantIndex.upcomingToday")}</p>
        <Badge variant="secondary" className="rounded-full">
          {upcoming.length}
        </Badge>
      </div>
      <ul className="mt-3 space-y-2">
        {upcoming.map((u) => (
          <li key={u.time} className="flex items-center gap-3 rounded-xl bg-surface p-3">
            <div className="grid place-items-center rounded-lg bg-background px-2.5 py-1.5 shadow-soft">
              <p className="font-display text-sm font-bold leading-none">{u.time}</p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">{u.dur}</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{t(`merchantIndex.spaces.${u.spaceKey}`)}</p>
              <p className="truncate text-xs text-muted-foreground">{u.guest}</p>
            </div>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </li>
        ))}
      </ul>
    </Card>
  );
}

const alerts = [
  { icon: AlertTriangle, tone: "amber", titleKey: "lowAvailability", bodyKey: "lowAvailabilityBody" },
  { icon: Bell, tone: "blue", titleKey: "newReview", bodyKey: "newReviewBody" },
  { icon: CheckCircle2, tone: "emerald", titleKey: "payoutSent", bodyKey: "payoutSentBody" },
];

function AlertsPanel() {
  const { t } = useTranslation();
  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{t("merchantIndex.alerts")}</p>
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
          {t("merchantIndex.markAllRead")}
        </Button>
      </div>
      <ul className="mt-3 space-y-2">
        {alerts.map((a) => {
          const Icon = a.icon;
          const tones: Record<string, string> = {
            amber: "bg-amber-500/10 text-amber-600",
            blue: "bg-sky-500/10 text-sky-600",
            emerald: "bg-emerald-500/10 text-emerald-600",
          };
          return (
            <li key={a.titleKey} className="flex items-start gap-3 rounded-xl border border-border/60 p-3">
              <div className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", tones[a.tone])}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{t(`merchantIndex.alertsData.${a.titleKey}`)}</p>
                <p className="text-xs text-muted-foreground">{t(`merchantIndex.alertsData.${a.bodyKey}`)}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
